import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import request from "../../../Helpers/API";
import Swal from "sweetalert2";

import { LoaderScreen } from "../../components/LoaderScreen";
import PlantillaScreenRemastered from "../PlantillaScreenRemastered";
import { CRM_DetalleResumen } from "./CRM_DetalleResumen";
import { CRM_DetalleGenerales } from "./CRM_DetalleGenerales";
import { CRM_DetalleCredito } from "./CRM_DetalleCredito";
import { CRM_DetalleUbicacion } from "./CRM_DetalleUbicacion";
import { CRM_DetalleBuroCredito } from "./CRM_DetalleBuroCredito";
import { CRM_DetalleFacturacion } from "./CRM_DetalleFacturacion";
import { CRM_DetalleClasificacion } from "./CRM_DetalleClasificacion";
import { CRM_DetalleCultivos } from "./CRM_DetalleCultivos";
import { CRM_DetalleContactos } from "./CRM_DetalleContactos";
import { CRM_DetalleEquipo } from "./CRM_DetalleEquipo";
import { CRM_DetalleEquip } from "./CRM_DetalleEquip";
import { ClientesDatosGenerales } from "../../Clientes/ClientesDatosGenerales";

// Rol Mercadotecnia — solo ve Datos generales, Facturación y Validación del expediente
const ROL_MERCADOTECNIA = "ADMK";
// Rol Crédito — solo ve Buró de crédito
const ROL_CREDITO = "COOC";
// Rol con acceso total — ve y edita todas las secciones sin restricción, ignora ADMK/COOC
const ROL_REWR = "REWR";

export const CRM_DetalleScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const { roles } = useSelector((store) => store.sesion);
  const esRolRewr = roles.some((rol) => rol.idrol === ROL_REWR);
  const esRolMercadotecnia = roles.some((rol) => rol.idrol === ROL_MERCADOTECNIA);
  const esRolCredito = roles.some((rol) => rol.idrol === ROL_CREDITO);

  // Las secciones exclusivas de ADMK (Generales/Facturación/Validación) y de COOC (Buró de
  // crédito) solo son editables por su rol dueño o por REWR (acceso total) — cualquier otro
  // usuario las ve pero en modo solo lectura, sin botón de Editar
  const soloLecturaSeccionAdmk = !esRolMercadotecnia;
  // COOC es dueño de esta sección; REWR (acceso total) también puede editarla, igual que ya
  // ocurre en el resto de secciones
  const soloLecturaSeccionCredito = !esRolCredito && !esRolRewr;
  const soloLectura = (esRolRewr || esRolCredito) && !esRolMercadotecnia;
  // Equip también es editable por COOC, además de quien ya puede editar el resto de secciones
  const soloLecturaSeccionEquip = soloLectura && !esRolCredito;
  // Asesores asignados solo lo edita ADMK (o REWR, acceso total) — ningún otro rol
  const soloLecturaSeccionAsesores = !esRolMercadotecnia && !esRolRewr;
  // idcliente llega por route state desde el buscador de CRM_Dashboard (history.push(ruta, { idcliente }))
  // Si no viene, la pantalla se comporta como alta de cliente nuevo con todos los campos vacíos
  const idcliente = location?.state?.idcliente || null;
  const esNuevo = !idcliente;

  const [cliente, setCliente] = useState(null);
  // Override local del tipo de persona: guardarClienteExistente() (CRM_DetalleGenerales) no
  // refresca "cliente" completo tras guardar (evita reventar el form de otras secciones que se
  // sincronizan por useEffect al cambiar sus props), así que sin esto, CRM_DetalleBuroCredito solo
  // reflejaba el tipoPersona que trajo la carga inicial de GetInfoClienteID. Se limpia cada vez que
  // esa carga real sí ocurre (abajo, en cargarInfoCliente), para que nunca quede una versión vieja
  // pisando un tipoPersona más reciente que sí vino del backend
  const [tipoPersonaLocal, setTipoPersonaLocal] = useState(null);
  const [opcionesGenerales, setOpcionesGenerales] = useState({ estatus: [], origen: [], tipo: [], clasificacion: [], tecnologia: [], etiqueta: [] });
  const [ubicacionCliente, setUbicacionCliente] = useState([]);
  const [opcionesUbicacion, setOpcionesUbicacion] = useState({ estado: [], municipio: [] });
  const [datosPersonaFisicaCliente, setDatosPersonaFisicaCliente] = useState(null);
  const [facturacionCliente, setFacturacionCliente] = useState(null);
  const [clasificacionCliente, setClasificacionCliente] = useState(null);
  const [opcionesClasificacion, setOpcionesClasificacion] = useState({ lineas: [], giros: [], superficie: [] });
  const [opcionesAsesores, setOpcionesAsesores] = useState([]);
  const [asesoresCliente, setAsesoresCliente] = useState([]);
  const [opcionesCultivo, setOpcionesCultivo] = useState({ terreno: [], riego: [] });
  const [cultivoCliente, setCultivoCliente] = useState([]);
  const [contactoCliente, setContactoCliente] = useState([]);
  const [equipCliente, setEquipCliente] = useState([]);
  const [validado, setValidado] = useState(null);
  const [responsableDepartamento, setResponsableDepartamento] = useState([]);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

  // Recarga toda la información del cliente desde el mismo endpoint que el mount inicial —
  // usada tanto al entrar a la pantalla como después de guardar cambios en cualquier sección
  const cargarInfoCliente = () => {
    if (!idcliente) {
      setCliente(null);
      setTipoPersonaLocal(null);
      setOpcionesGenerales({ estatus: [], origen: [], tipo: [], clasificacion: [], tecnologia: [], etiqueta: [] });
      setUbicacionCliente([]);
      setOpcionesUbicacion({ estado: [], municipio: [] });
      setDatosPersonaFisicaCliente(null);
      setFacturacionCliente(null);
      setClasificacionCliente(null);
      setOpcionesClasificacion({ lineas: [], giros: [], superficie: [] });
      setOpcionesAsesores([]);
      setAsesoresCliente([]);
      setOpcionesCultivo({ terreno: [], riego: [] });
      setCultivoCliente([]);
      setContactoCliente([]);
      setEquipCliente([]);
      setValidado(null);
      setResponsableDepartamento([]);
      return Promise.resolve();
    }
    setLoader((prev) => ({ ...prev, mostrar: true }));
    return request
      .get(`Clientes/GetInfoClienteID?idcliente=${Number(idcliente)}`)
      .then((result) => {
        setCliente(result?.info_General_cliente || null);
        // Cualquier override local queda obsoleto en cuanto llega un tipoPersona real del backend
        setTipoPersonaLocal(null);
        setOpcionesGenerales({
          estatus: result?.opciones_estatus || [],
          origen: result?.opciones_origen || [],
          tipo: result?.opciones_tipo || [],
          clasificacion: result?.opciones_clasificacion || [],
          tecnologia: result?.opciones_tecnologia || [],
          etiqueta: result?.opciones_etiqueta || [],
        });
        setUbicacionCliente(result?.info_ubicacion_cliente || []);
        setOpcionesUbicacion({
          estado: result?.opciones_estado || [],
          municipio: result?.opciones_municipio || [],
        });
        // Solo viene cuando el cliente es Persona Física y ya tiene datos capturados; si el
        // backend no lo manda, CRM_DetalleBuroCredito deja sus campos vacíos (null)
        setDatosPersonaFisicaCliente(result?.datos_persona_fisica || null);
        setFacturacionCliente(result?.info_Facturacion_cliente || null);
        setClasificacionCliente(result?.info_clasificacion_cliente || null);
        setOpcionesClasificacion({
          lineas: result?.opciones_lineas || [],
          giros: result?.opciones_giros || [],
          superficie: result?.opciones_superficie || [],
        });
        setOpcionesAsesores(result?.opciones_asesor || []);
        setAsesoresCliente(result?.info_asesores_cliente || []);
        setOpcionesCultivo({
          terreno: result?.opciones_cultivo_terreno || [],
          riego: result?.opciones_cultivo_riego || [],
        });
        setCultivoCliente(result?.info_cultivo_cliente || []);
        setContactoCliente(result?.info_contacto_cliente || []);
        setEquipCliente(result?.info_equip_cliente || []);
        setValidado(result?.validado || null);
        setResponsableDepartamento(result?.responsable_departamento || []);
        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("CLIENTES", JSON.stringify(error), "error");
      });
  };

  useEffect(() => {
    cargarInfoCliente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idcliente]);

  // Datos generales llama esto tras registrar exitosamente al cliente nuevo
  const handleClienteRegistrado = (idclienteNuevo) => {
    if (idclienteNuevo) {
      history.replace("/DashClientes/Detalle", { idcliente: idclienteNuevo });
    } else {
      // La API no devolvió el idcliente — regresamos al buscador para localizarlo, igual que el flujo original
      history.replace("/DashClientes/Main");
    }
  };


  return (
    <PlantillaScreenRemastered modulo="Clientes" titulo={esNuevo ? "Nuevo cliente" : cliente?.razon_social || "Registro de cliente"} idcliente={idcliente} >
      <LoaderScreen loader={loader} />
      <div className="crm-detalle-wrapper">
        <CRM_DetalleResumen esNuevo={esNuevo} />

        <CRM_DetalleGenerales
          esNuevo={esNuevo}
          cliente={cliente}
          idcliente={idcliente}
          opcionesGenerales={opcionesGenerales}
          opcionesUbicacion={opcionesUbicacion}
          onClienteRegistrado={handleClienteRegistrado}
          onTipoPersonaGuardado={setTipoPersonaLocal}
          soloLectura={soloLecturaSeccionAdmk}
          validado={validado}
          responsableDepartamento={responsableDepartamento}
        />

        {/* Alta de cliente nuevo — solo se captura Datos generales; el resto del expediente aparece al registrar */}
        {!esNuevo && (
          <>
            <CRM_DetalleCredito
              esNuevo={esNuevo}
              idcliente={idcliente}
              opcionesGenerales={opcionesGenerales}
              cliente={cliente}
              soloLectura={soloLectura}
              responsableDepartamento={responsableDepartamento}
            />
            <CRM_DetalleUbicacion
              esNuevo={esNuevo}
              idcliente={idcliente}
              ubicacionCliente={ubicacionCliente}
              opcionesUbicacion={opcionesUbicacion}
              setLoader={setLoader}
              soloLectura={soloLectura}
              responsableDepartamento={responsableDepartamento}
            />
            <CRM_DetalleBuroCredito
              esNuevo={esNuevo}
              idcliente={idcliente}
              tipoPersona={tipoPersonaLocal ?? cliente?.tipo_persona ?? "M"}
              datosPersonaFisica={datosPersonaFisicaCliente}
              ubicacionCliente={ubicacionCliente}
              opcionesUbicacion={opcionesUbicacion}
              setLoader={setLoader}
              onGuardado={setUbicacionCliente}
              soloLectura={soloLecturaSeccionCredito}
              responsableDepartamento={responsableDepartamento}
            />

            <CRM_DetalleFacturacion
              esNuevo={esNuevo}
              idcliente={idcliente}
              cliente={cliente}
              facturacionCliente={facturacionCliente}
              soloLectura={soloLecturaSeccionAdmk}
              responsableDepartamento={responsableDepartamento}
            />

            <CRM_DetalleClasificacion
              esNuevo={esNuevo}
              idcliente={idcliente}
              clasificacionCliente={clasificacionCliente}
              opcionesClasificacion={opcionesClasificacion}
              opcionesGenerales={opcionesGenerales} z
              soloLectura={soloLectura}
              responsableDepartamento={responsableDepartamento}
            />
            <CRM_DetalleCultivos
              esNuevo={esNuevo}
              idcliente={idcliente}
              cultivoCliente={cultivoCliente}
              opcionesCultivo={opcionesCultivo}
              soloLectura={soloLectura}
              responsableDepartamento={responsableDepartamento}
            />
            <CRM_DetalleContactos esNuevo={esNuevo} idcliente={idcliente} contactoCliente={contactoCliente} soloLectura={soloLectura} responsableDepartamento={responsableDepartamento} />
            <CRM_DetalleEquipo
              esNuevo={esNuevo}
              idcliente={idcliente}
              asesoresCliente={asesoresCliente}
              opcionesAsesores={opcionesAsesores}
              opcionesLineas={opcionesClasificacion.lineas}
              soloLectura={soloLecturaSeccionAsesores}
              responsableDepartamento={responsableDepartamento}
            />
            <CRM_DetalleEquip esNuevo={esNuevo} idcliente={idcliente} equipCliente={equipCliente} soloLectura={soloLecturaSeccionEquip} responsableDepartamento={responsableDepartamento} />
          </>
        )}
      </div>
    </PlantillaScreenRemastered>
  );
};
